/**
 * @fileoverview DynamicImage.jsx
 * 
 * © 2026 KITH Travels (Pvt) Ltd. All rights reserved.
 * This software is the confidential and proprietary information of KITH Travels.
 * You shall not disclose such Confidential Information and shall use it only in 
 * accordance with the terms of the license agreement you entered into with KITH Travels.
 */
import React, { useState, useEffect } from 'react'

/**
 * DynamicImage Component
 * Handles progressive image loading with a low-res placeholder.
 * Renders both images layered on top of each other to avoid layout shift (CLS).
 */
function DynamicImage({ 
  loading: externalLoading, 
  setLoading: setExternalLoading, 
  LowImage, 
  HighImage, 
  image,
  alt,
  className = "",
  imgClassName = "",
  loadingAttr = "lazy",
  fetchPriority
}) {
  const [highResLoaded, setHighResLoaded] = useState(false);

  // Sync internal loaded state with external loading state if provided
  useEffect(() => {
    if (externalLoading === false) {
      setHighResLoaded(true);
    }
  }, [externalLoading]);

  const handleHighResLoad = () => {
    setHighResLoaded(true);
    if (setExternalLoading) {
      setExternalLoading(false);
    }
  };

  const lowSrc = LowImage || (image?.l ? image.l : image)
  const highSrc = HighImage || (image?.h ? image.h : image)

  return (
    <div className={`relative w-full h-full overflow-hidden bg-gray-100 ${className}`}>
      {/* Low-res placeholder - blurred and fades out once high-res is loaded */}
      {lowSrc && (
        <img
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out ${imgClassName} ${
            highResLoaded ? 'opacity-0 pointer-events-none' : 'opacity-100 blur-lg scale-110'
          }`}
          src={lowSrc}
          alt={alt || 'KITH Travels Sri Lanka'}
          loading={loadingAttr}
        />
      )}
      
      {/* High-res image - fades in once loaded */}
      <img
        className={`w-full h-full object-cover transition-opacity duration-700 ease-in-out ${imgClassName} ${
          highResLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        src={highSrc}
        alt={alt || 'KITH Travels Sri Lanka'}
        onLoad={handleHighResLoad}
        loading={loadingAttr}
        fetchPriority={fetchPriority}
      />
    </div>
  )
}

export default DynamicImage